import { NavLink, Outlet, Navigate, useLocation } from 'react-router-dom';
import { PageHeader } from '@solid/ui';
import {
  User,
  Bell,
  Users,
  Plug,
  KeyRound,
  Database,
  Columns3,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore, getIsAgencyAdmin } from '@/stores/auth.store';

interface SettingsNavItem {
  to: string;
  icon: React.ElementType;
  label: string;
  description: string;
  adminOnly?: boolean;
}

const navItems: SettingsNavItem[] = [
  {
    to: '/settings/profile',
    icon: User,
    label: 'Profile',
    description: 'Your personal information',
  },
  {
    to: '/settings/notifications',
    icon: Bell,
    label: 'Notifications',
    description: 'Email and push preferences',
  },
  {
    to: '/settings/team',
    icon: Users,
    label: 'Team',
    description: 'Manage team members',
    adminOnly: true,
  },
  {
    to: '/settings/statuses',
    icon: Columns3,
    label: 'Statuses',
    description: 'Task workflow statuses',
    adminOnly: true,
  },
  {
    to: '/settings/integrations',
    icon: Plug,
    label: 'Integrations',
    description: 'Connect external services',
    adminOnly: true,
  },
  {
    to: '/settings/api-keys',
    icon: KeyRound,
    label: 'API Keys',
    description: 'Service keys for other apps',
    adminOnly: true,
  },
  {
    to: '/settings/data',
    icon: Database,
    label: 'Data',
    description: 'Export and import projects',
    adminOnly: true,
  },
];

export function Settings() {
  const location = useLocation();
  const { user } = useAuthStore();

  // Check if user is an agency admin or owner
  const isAgencyAdmin = getIsAgencyAdmin(user);

  // Filter nav items based on admin status
  const filteredNavItems = navItems.filter(item => !item.adminOnly || isAgencyAdmin);

  // Redirect /settings to /settings/profile
  if (location.pathname === '/settings') {
    return <Navigate to="/settings/profile" replace />;
  }

  return (
    <div className="w-full">
      <PageHeader
        title="Settings"
        subtitle="Manage your account settings and preferences"
      />

      <div className="flex flex-col-reverse lg:flex-row gap-6 lg:gap-8">
        {/* Content Area */}
        <div className="flex-1 min-w-0">
          <Outlet />
        </div>

        {/* Sidebar Navigation */}
        {/* shrink-0 is a DESKTOP rule: on a phone this nav is a horizontal
            scroller, and refusing to shrink made the row widen the page
            instead of scrolling inside itself (mobile audit, 2026-08-27). */}
        <nav className="w-full min-w-0 lg:w-56 lg:shrink-0">
          <ul className="flex min-w-0 lg:flex-col gap-1 overflow-x-auto no-scrollbar lg:overflow-visible">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors whitespace-nowrap shrink-0',
                        isActive
                          ? 'bg-accent/10 text-accent'
                          : 'text-grey-600 hover:bg-grey-50 hover:text-secondary'
                      )
                    }
                  >
                    <Icon className="w-5 h-5 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium text-sm">{item.label}</div>
                    </div>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </div>
  );
}
